import React from 'react';

import { useTranslation } from 'react-i18next';
import {
  QAsyncMultiSelect,
  QIcon,
  QSelectItem,
  QSelectPlaceholder,
  QText,
  SearchResource,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { SuggestionApi } from '../../../types/suggestionApi';
import * as DisplayStrings from '../../../displayStrings';
import { ControllerFieldState } from 'react-hook-form';
import { SuggestionsOption } from '../../../types/suggestion';

export type ResourceSelectItem = QSelectItem & {
  code?: string;
  title?: string;
  resource?: SearchResource;
};

type Props = {
  onChange: (selected: QSelectItem[]) => void;
  defaults?: ResourceSelectItem[];
  suggestionApi: SuggestionApi;
  valuePropFromSuggestion: 'uuid' | 'qri' | 'id';
  domain: 'suppliers' | 'documents' | 'events' | 'documents or events';
  debounceInterval?: number;
  omitFromSuggestionResults?: string[] | number[];
  fieldState?: ControllerFieldState;
  renderResourceLabel?: boolean;
};

type AsyncMultiSelectProps = {
  defaultValue?: ResourceSelectItem[];
  loadOptions: (term: string) => Promise<ResourceSelectItem[]>;
  onChange: (selected: ResourceSelectItem[]) => void;
  noOptionsMessage: () => React.ReactNode;
  isInvalid?: boolean;
  menuPosition?: 'fixed' | 'absolute';
  children?: React.ReactNode;
} & Partial<ControllerFieldState>;

const AsyncMultiSelect = QAsyncMultiSelect as unknown as (props: AsyncMultiSelectProps) => React.ReactElement;

const MultiSelectWithSuggestions: React.FC<Props> = ({
  onChange,
  defaults,
  suggestionApi,
  valuePropFromSuggestion,
  domain,
  debounceInterval,
  omitFromSuggestionResults,
  fieldState,
  renderResourceLabel,
}) => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();

  const waitFor = debounceInterval ?? 500;
  let searched = false;

  const handleLoadOptions = () => {
    let timeout: ReturnType<typeof setTimeout>;
    let resolve: (value: ReturnType<typeof loadSuggestions>) => void;
    let reject: (value: unknown) => void;
    let response: ReturnType<typeof loadSuggestions> = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const debouncedLoadSuggestions = async (inputValue: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        try {
          resolve(loadSuggestions(inputValue));
        } catch (e) {
          showToast({
            title: DisplayStrings.SearchFailed,
            description: DisplayStrings.DefaultErrorMessage,
            status: 'error',
          });
          reject(e);
        }
        response = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
      }, waitFor);
      return response;
    };
    return debouncedLoadSuggestions;
  };

  const toDisplayOption = (option: ResourceSelectItem): ResourceSelectItem => {
    if (!option.code) {
      return option;
    }
    return renderResourceLabel ? { ...option, label: option.title ?? option.label } : { ...option, code: undefined };
  };

  const omitFromResults = omitFromSuggestionResults?.map((v) => String(v)) ?? [];

  const loadSuggestions = async (inputValue: string) => {
    searched = true;
    if (inputValue.length < 1) {
      return [];
    }
    const data = await suggestionApi.suggest(companyId, inputValue);
    return data
      .filter((item) => {
        let id;
        if (item && 'identifiers' in item) {
          // check if item is a quick search result
          id = item?.identifiers.id;
        } else {
          id = item?.uuid;
        }
        if (!id) {
          return true;
        }
        return !omitFromResults.includes(String(id));
      })
      .map((item): ResourceSelectItem => {
        let label;
        let value;
        let code: string | undefined;
        let title: string | undefined;
        let resource: SearchResource | undefined;
        if ('identifiers' in item) {
          // check if item is a quick search result
          label = item.displayLabel;
          value = item.identifiers.qri;
          if (valuePropFromSuggestion === 'id') {
            value = item.identifiers.id;
          }
          code = item.identifiers.code ?? undefined;
          title = item.name;
          resource = item.resource as SearchResource;
        } else {
          label = item.code ? `${item.code} ${item.title}` : item.title;
          value = item.uuid;
          code = item.code ?? undefined;
          title = item.title;
        }
        return toDisplayOption({
          ...SuggestionsOption.parse({ label, value }),
          code,
          title,
          resource,
        });
      });
  };

  const noOptionsMessage = () => {
    const text = searched
      ? t('common.placeholders.noResults')
      : `${t('common.placeholders.startTypingToSearchFor')} ${domain}`;
    return <QText>{text}</QText>;
  };

  return (
    <AsyncMultiSelect
      defaultValue={defaults?.map(toDisplayOption)}
      loadOptions={handleLoadOptions()}
      onChange={onChange}
      noOptionsMessage={noOptionsMessage}
      isInvalid={fieldState?.invalid}
      {...fieldState}
      menuPosition="fixed"
    >
      <QSelectPlaceholder>
        <QIcon iconName="Search" />
        <QText>{t('common.placeholders.searchFor', { domain })}</QText>
      </QSelectPlaceholder>
    </AsyncMultiSelect>
  );
};

export default MultiSelectWithSuggestions;
